import { useSyncExternalStore } from 'react';
import { z } from 'zod';

/**
 * Tells a tab that a different frontend bundle is available.
 *
 * API activity checks the deployed config at most once a minute. Its
 * bundleHash covers the frontend files and excludes the release SHA, so a
 * backend-only release does not interrupt users. A different hash must last
 * through the ten-minute wait before the tab offers a reload.
 *
 * A matching hash clears the notice, including after a rollback. If a reload
 * returns the same bundle, the tab remembers that attempt and does not ask
 * again for that pair of bundles.
 */

// Wait ten minutes for a release to settle, grouping releases that arrive
// close together into one notice.
export const RELEASE_MISMATCH_GRACE_MS = 10 * 60 * 1000;
export const WEB_RELEASE_CHECK_INTERVAL_MS = 60 * 1000;
const WEB_RELEASE_CHECK_TIMEOUT_MS = 10 * 1000;

// Deployments publish a SHA-256 of the frontend files. Development uses
// `__DEV__`, where Vite already updates the page through HMR.
const BUNDLE_HASH_PATTERN = /^[0-9a-f]{64}$/;
const webReleaseSchema = z.object({
    bundleHash: z.string().regex(BUNDLE_HASH_PATTERN),
});

const RELOAD_STORAGE_KEY = 'phoebe-release-update-reload';
const RELOAD_QUERY_PARAM = '__phoebe_reload';

const reloadRecordSchema = z.object({
    bundleHash: z.string(),
    availableBundleHash: z.string(),
});

type ReloadRecord = z.infer<typeof reloadRecordSchema>;

type PendingMismatch = {
    readonly bundleHash: string;
    readonly firstSeenAtMs: number;
};

let pendingMismatch: PendingMismatch | null = null;
let availableBundleHash: string | null = null;
let dismissedBundleHash: string | null = null;
let observedBundleHash: string | null = null;
let lastCheckAtMs: number | null = null;
let checkInFlight = false;
// undefined: not yet read from sessionStorage. null: no usable record.
let lastReload: ReloadRecord | null | undefined;
const listeners = new Set<() => void>();

function readLastReload(): ReloadRecord | null {
    if (lastReload === undefined) {
        try {
            const raw = sessionStorage.getItem(RELOAD_STORAGE_KEY);
            const result = reloadRecordSchema.safeParse(
                raw === null ? null : JSON.parse(raw),
            );
            lastReload = result.success ? result.data : null;
        } catch {
            // Blocked storage or a malformed value: prompt as usual.
            lastReload = null;
        }
    }
    return lastReload;
}

function emitChange(): void {
    for (const listener of listeners) {
        listener();
    }
}

function setAvailableBundleHash(bundleHash: string | null): void {
    if (bundleHash === availableBundleHash) {
        return;
    }
    availableBundleHash = bundleHash;
    emitChange();
}

export function observeWebBundleHash(
    bundleHash: string,
    deployedBundleHash: string | null,
): void {
    if (
        deployedBundleHash === null ||
        !BUNDLE_HASH_PATTERN.test(deployedBundleHash) ||
        !BUNDLE_HASH_PATTERN.test(bundleHash)
    ) {
        return;
    }
    observedBundleHash = bundleHash;
    if (deployedBundleHash === bundleHash) {
        pendingMismatch = null;
        setAvailableBundleHash(null);
        return;
    }

    const nowMs = Date.now();
    if (pendingMismatch?.bundleHash !== deployedBundleHash) {
        // A different bundle restarts the clock but keeps an update that is
        // already available: this tab is still behind while a later
        // release cuts over.
        pendingMismatch = {
            bundleHash: deployedBundleHash,
            firstSeenAtMs: nowMs,
        };
        return;
    }
    if (nowMs - pendingMismatch.firstSeenAtMs < RELEASE_MISMATCH_GRACE_MS) {
        return;
    }
    const reload = readLastReload();
    if (
        reload?.bundleHash === bundleHash &&
        reload.availableBundleHash === deployedBundleHash
    ) {
        // This tab already reloaded for this release and got the same
        // bundle back, so another reload would not help.
        return;
    }
    setAvailableBundleHash(deployedBundleHash);
}

export async function checkForWebUpdate(bundleHash: string): Promise<void> {
    const nowMs = Date.now();
    if (
        !BUNDLE_HASH_PATTERN.test(bundleHash) ||
        checkInFlight ||
        (lastCheckAtMs !== null &&
            nowMs - lastCheckAtMs < WEB_RELEASE_CHECK_INTERVAL_MS)
    ) {
        return;
    }
    lastCheckAtMs = nowMs;
    checkInFlight = true;
    const controller = new AbortController();
    const timeout = setTimeout(
        () => controller.abort(),
        WEB_RELEASE_CHECK_TIMEOUT_MS,
    );
    try {
        const response = await fetch('/config.json', {
            cache: 'no-store',
            signal: controller.signal,
        });
        if (!response.ok) {
            return;
        }
        const release = webReleaseSchema.parse(await response.json());
        observeWebBundleHash(bundleHash, release.bundleHash);
    } catch {
        // A failed version check must not interrupt the API request. The next
        // API activity after the interval can try again.
    } finally {
        clearTimeout(timeout);
        checkInFlight = false;
    }
}

export function reloadForRelease(bundleHash: string): void {
    if (observedBundleHash !== null) {
        try {
            sessionStorage.setItem(
                RELOAD_STORAGE_KEY,
                JSON.stringify({
                    bundleHash: observedBundleHash,
                    availableBundleHash: bundleHash,
                } satisfies ReloadRecord),
            );
        } catch {
            // Without the record the tab may prompt again; still reload.
        }
    }
    // A new document URL bypasses a cached copy in Chrome and Edge, where
    // JavaScript cannot request the browser's hard-reload command. Assets
    // already have content hashes and config.json is fetched with no-store.
    const url = new URL(window.location.href);
    url.searchParams.set(RELOAD_QUERY_PARAM, crypto.randomUUID());
    window.location.replace(url.toString());
}

/** Remove the navigation's temporary cache marker before the router starts. */
export function clearReleaseReloadMarker(): void {
    const url = new URL(window.location.href);
    if (url.searchParams.has(RELOAD_QUERY_PARAM)) {
        url.searchParams.delete(RELOAD_QUERY_PARAM);
        window.history.replaceState(window.history.state, '', url.toString());
    }
}

export function dismissReleaseUpdate(bundleHash: string): void {
    dismissedBundleHash = bundleHash;
    emitChange();
}

function getVisibleBundleHash(): string | null {
    return availableBundleHash === dismissedBundleHash
        ? null
        : availableBundleHash;
}

function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/** The frontend bundle this tab should reload for, or null. */
export function useAvailableBundleHash(): string | null {
    return useSyncExternalStore(subscribe, getVisibleBundleHash, () => null);
}

/** Resets in-memory state, as a page reload does; sessionStorage is kept. */
export function resetReleaseUpdateStoreForTests(): void {
    pendingMismatch = null;
    availableBundleHash = null;
    dismissedBundleHash = null;
    observedBundleHash = null;
    lastCheckAtMs = null;
    checkInFlight = false;
    lastReload = undefined;
}
